<template>
  <div class="tile">
    <ContentfulDynamicRenderer v-bind="content"></ContentfulDynamicRenderer>
  </div>
</template>

<script setup>
import { toRefs } from 'vue';
import useGridTile from '@/compositions/useGridTile';
import ContentfulDynamicRenderer from '@/components/contentful/Fields/ContentfulDynamicRenderer.vue';

const props = defineProps({
  content: {
    type: Object,
    required: true,
  },
  size: {
    type: Object,
    required: true,
    default: [{ ratio320px: '', ratio768px: '', ratio1200px: '', ratio1400px: '', ratio1800px: '' }],
  },
  productsCount: {
    type: Number,
    required: true,
    default: 0,
  },
  productGridColCount: {
    type: Number,
    required: true,
    default: 0,
  },
});

const { productsCount, productGridColCount } = toRefs(props);

const { displayTiles } = useGridTile({
  tileSizes: props.size,
  productsCount,
  productGridColCount,
});

</script>

<style lang="postcss" scoped>
@import '@/pixel/style/variables';

.tile {
  z-index: 1;
  padding: var(--space-7);
  overflow: hidden;

  ::v-deep(.contentful-richtext .ctf-heading) {
    font-size: var(--font-size-24);
  }

  ::v-deep(.grid-row .grid-row-item .contentful-richtext) {
    margin: var(--space-2) 0 0 0;
    width: 100%;
  }

  ::v-deep(.ctf-link) {
    font-size: var(--font-size-16);
    font-weight: bold;
  }

  ::v-deep(.contentful-richtext .paragraph) {
    font-size: var(--font-size-14);
    margin-top: var(--space-2);
    color: var(--grey-medium);
  }

  @media (max-width: $extra-small-breakpoint) {
    display: v-bind(displayTiles.ratio320px);
    grid-area: v-bind(props.size.ratio320px);
  }

  @media (min-width: $extra-small-breakpoint) {
    display: v-bind(displayTiles.ratio768px);
    grid-area: v-bind(props.size.ratio768px);
  }

  @media (min-width: $small-breakpoint) {
    display: v-bind(displayTiles.ratio768px);
    grid-area: v-bind(props.size.ratio768px);

    ::v-deep(.contentful-richtext.ctf-heading-casing-normalcase .ctf-body) {
      max-width: 100%;
    }
  }

  @media (min-width: $mobile-breakpoint) {
    display: v-bind(displayTiles.ratio1200px);
    grid-area: v-bind(props.size.ratio1200px);

    ::v-deep(.contentful-richtext.ctf-heading-casing-normalcase .ctf-body) {
      max-width: 30em;
    }
  }

  @media (min-width: $tablet-breakpoint) {
    display: v-bind(displayTiles.ratio1200px);
    grid-area: v-bind(props.size.ratio1200px);
  }

  @media (min-width: 1367px) {
    display: v-bind(displayTiles.ratio1400px);
    grid-area: v-bind(props.size.ratio1400px);
  }

  @media (min-width: $extra-large-breakpoint) {
    display: v-bind(displayTiles.ratio1800px);
    grid-area: v-bind(props.size.ratio1800px);

    ::v-deep(.contentful-richtext.ctf-heading-casing-normalcase .ctf-body) {
      max-width: 34em;
    }
  }
}
</style>
